import { useWorkspaceText } from './interface-copy';
// The Skill canvas. A Skill is the company's written know-how for one kind of
// work: instructions that build on its knowledge, which any chat or agent run
// finds by its Use when line and follows. It never runs on its own, so there is
// no Try, model or run here: changes proposed in chat save after approval,
// and publication is a separate, deliberate step.
//
// Opened from an answer's "Followed N Skills" box (followed-skills.tsx), the
// canvas shows the exact version that was followed and why.
import { useEffect, useRef, useState } from 'react';
import { Tabs } from 'radix-ui';
import { useCompany } from '@/company/context';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import skillsCopy from '@/i18n/ns/skills';
import { workspaceApi } from './api';
import { CanvasActivityTab } from './canvas-activity-tab';
import { useCanvasCopy } from './canvas-copy';
import { CanvasFooter } from './canvas-footer';
import { useWorkspace } from './context';
import { DocumentDiff } from './document-diff-view';
import { documentDiffCopy } from './document-diff-copy';
import { followedReason } from './followed-skills';
import { WorkspaceIcon } from './icons';
import { livingApi, type ItemLinks, type LinkEnd } from './living-api';
import { WorkspaceMarkdown } from './markdown';
import { routeFor } from './navigation';
import { NewObjectCanvas } from './primitives';
import { linkLabel } from './provenance-panel';
import { DeleteSkill } from './skill-archive';
import { useSkillChanges } from './skill-changes';
import type { CanvasTarget, CompanySkill, SkillSnapshot } from './types';
import './skill-canvas.css';

type Copy = ReturnType<typeof useNamespaceCopy<keyof (typeof skillsCopy)['en']>>;
type SkillTab = 'instructions' | 'changes' | 'followed' | 'versions' | 'activity';

function revisionLabel(copy: Copy, skill: CompanySkill, revision: number) {
  const template =
    revision === skill.publishedRevision
      ? copy.versionPublished
      : revision === skill.currentRevision
        ? copy.versionDraft
        : copy.versionEarlier;
  return formatCopy(template, { revision });
}

/** The Skill's saved revisions, read again when a new one is saved, and whether that read is done. */
function useSkillHistory(skill: CompanySkill) {
  const [history, setHistory] = useState<SkillSnapshot[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setState('loading');
    void workspaceApi
      .revisions(skill.id)
      .then((value) => {
        if (!active) return;
        setHistory(value);
        setState('ready');
      })
      .catch(() => {
        if (active) setState('failed');
      });
    return () => {
      active = false;
    };
  }, [skill.id, skill.currentRevision, attempt]);
  const retry = () => {
    setState('loading');
    setAttempt((value) => value + 1);
  };
  return { history, state, retry };
}

/** Where the Skill was followed (the item links "Used by"), read again when the workspace changes. */
function useSkillUsage(id: string) {
  const ws = useWorkspace();
  const [links, setLinks] = useState<ItemLinks | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setFailed(false);
    livingApi
      .links('skill', id)
      .then((value) => {
        if (active) setLinks(value);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [id, ws.data]);
  return { links, failed };
}

const answersLine = (copy: Copy, answers: number) =>
  formatCopy(answers === 1 ? copy.followedInAnswersOne : copy.followedInAnswersOther, {
    count: answers,
  });

function SkillProperties({ copy, draft }: { copy: Copy; draft: SkillSnapshot }) {
  const ws = useWorkspace();
  return (
    <dl className="ws-skill-props">
      <dt>{copy.applies}</dt>
      <dd>
        <span className="ws-skill-applies">{copy.appliesWhenItFits}</span>
        <small>{copy.appliesHint}</small>
      </dd>
      {draft.fileIds.length > 0 && (
        <>
          <dt>{copy.filesRow}</dt>
          <dd className="ws-skill-chips">
            {draft.fileIds.map((id) => (
              <button
                type="button"
                key={id}
                className="ws-skill-ref"
                data-canvas-trigger="true"
                onClick={() => ws.openCanvas({ kind: 'file', id })}
              >
                <WorkspaceIcon name="file" size={13} />
                {ws.data.files.find((file) => file.id === id)?.name ?? copy.companyFile}
              </button>
            ))}
          </dd>
        </>
      )}
    </dl>
  );
}

function FollowedIn({
  copy,
  links,
  failed,
}: {
  copy: Copy;
  links: ItemLinks | null;
  failed: boolean;
}) {
  const { text: uiText } = useWorkspaceText();
  const ws = useWorkspace();
  if (failed) return <p className="ws-skill-quiet">{copy.followedInFailed}</p>;
  if (!links) return null;
  const answers = links.usage?.answers ?? 0;
  const open = (end: LinkEnd) =>
    end.kind === 'chat'
      ? ws.openThread(end.id)
      : end.kind === 'agent' || end.kind === 'result'
        ? ws.openCanvas({ kind: end.kind, id: end.id })
        : undefined;
  if (!answers && !links.usedBy.length)
    return <p className="ws-skill-quiet">{copy.followedInEmpty}</p>;
  return (
    <section className="ws-skill-followed">
      {answers > 0 && <p className="ws-skill-usage">{answersLine(copy, answers)}</p>}
      {links.usedBy.map((end) => (
        <button
          type="button"
          key={`${end.kind}:${end.id}`}
          className="ws-skill-place"
          onClick={() => open(end)}
        >
          <WorkspaceIcon name={end.kind === 'agent' ? 'agents' : 'chat'} size={15} />
          <span>{linkLabel(end, ws.user?.id, uiText)}</span>
          <WorkspaceIcon name="chevron" size={14} />
        </button>
      ))}
    </section>
  );
}

function Versions({
  copy,
  skill,
  history,
  viewing,
  busy,
  onView,
  onRestore,
}: {
  copy: Copy;
  skill: CompanySkill;
  history: SkillSnapshot[];
  viewing: number;
  busy: boolean;
  onView: (revision: number) => void;
  onRestore: () => void;
}) {
  const locale = useChatInterfaceLocale();
  return (
    <ol className="ws-skill-versions">
      {[...history].reverse().map((snapshot) => (
        <li key={snapshot.revision} aria-current={snapshot.revision === viewing}>
          <span>
            <b>{revisionLabel(copy, skill, snapshot.revision)}</b>
            <small>{new Date(snapshot.createdAt).toLocaleDateString(locale)}</small>
          </span>
          {snapshot.revision === viewing ? (
            <em>{copy.versionsCurrent}</em>
          ) : (
            <button
              type="button"
              className="ws-text-button"
              onClick={() => onView(snapshot.revision)}
            >
              {copy.versionsView}
            </button>
          )}
          {snapshot.revision === viewing &&
            snapshot.revision !== skill.currentRevision &&
            skill.canEdit && (
              <button type="button" className="ws-text-button" disabled={busy} onClick={onRestore}>
                {copy.restore}
              </button>
            )}
        </li>
      ))}
    </ol>
  );
}

function activeTabFor(target: CanvasTarget, hasChanges: boolean, hasVersions: boolean): SkillTab {
  if (target.tab === 'activity' && target.activityMessageId) return 'activity';
  if (target.tab === 'changes' && hasChanges) return 'changes';
  if (target.tab === 'followed') return 'followed';
  if (target.tab === 'versions' && hasVersions) return 'versions';
  return 'instructions';
}

function SkillDocument({ skill, target }: { skill: CompanySkill; target: CanvasTarget }) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(skillsCopy);
  const canvasCopy = useCanvasCopy();
  const diffCopy = documentDiffCopy(useChatInterfaceLocale());
  const company = useCompany().data;
  const canPublish = company?.membership?.permissions?.publishCompany === true;
  const { history } = useSkillHistory(skill);
  const { links, failed } = useSkillUsage(skill.id);
  // A target may name an earlier version to open (a followed Skill's "View version N").
  const requested = 'revision' in target ? Number(target.revision) : Number.NaN;
  const opened =
    Number.isInteger(requested) && requested > 0 && requested !== skill.currentRevision
      ? requested
      : null;
  const [viewing, setViewing] = useState<number | null>(opened);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [selection, setSelection] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const content = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setSelection('');
    setViewing(opened);
  }, [skill.id, skill.currentRevision, opened]);
  const draft = history.find((snapshot) => snapshot.revision === viewing) ?? skill.draft;
  const old = draft.revision !== skill.currentRevision;
  const changes = useSkillChanges(draft, skill.published);
  const published = skill.publishedRevision === skill.currentRevision;
  const activeTab = activeTabFor(target, Boolean(changes), history.length > 1);
  const answers = links?.usage?.answers ?? 0;
  const boundToThisSkill =
    ws.turnContext.intent.mode === 'skill-author' && ws.turnContext.intent.skillId === skill.id;
  const tab = (next: string) => ws.openCanvas({ ...target, tab: next });
  async function publish() {
    setBusy(true);
    setError('');
    try {
      const saved = await workspaceApi.publishSkill(skill.id, skill.currentRevision);
      await ws.refresh();
      ws.notify(
        saved.status === 'pending' || saved.skill?.status === 'pending'
          ? copy.requested
          : copy.published,
      );
    } catch {
      setError(copy.publishFailed);
    } finally {
      setBusy(false);
    }
  }
  async function restore() {
    setBusy(true);
    setError('');
    try {
      const restored = await workspaceApi.restoreSkill(skill.id, skill.currentRevision, draft);
      ws.acceptSkill(restored.skill);
      setViewing(null);
      await ws.refresh();
      ws.notify(copy.restored);
    } catch {
      setError(copy.restoreFailed);
    } finally {
      setBusy(false);
    }
  }
  async function unpublish() {
    setBusy(true);
    setError('');
    try {
      await workspaceApi.unpublishSkill(skill.id);
      setWithdrawing(false);
      await ws.refresh();
      ws.notify(copy.unpublished);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : copy.unpublishFailed);
    } finally {
      setBusy(false);
    }
  }
  function selectPassage() {
    const selected = window.getSelection();
    const text = selected?.toString().trim() ?? '';
    setSelection(
      !old &&
        content.current?.contains(selected?.anchorNode ?? null) &&
        content.current?.contains(selected?.focusNode ?? null) &&
        text.length > 3
        ? text.slice(0, 2000)
        : '',
    );
  }
  const editable = skill.canEdit && !boundToThisSkill;
  return (
    <Tabs.Root className="ws-skill-canvas" value={activeTab} onValueChange={tab}>
      <header className="ws-canvas-body ws-canvas-heading ws-skill-heading">
        <div className="ws-skill-meta">{revisionLabel(copy, skill, draft.revision)}</div>
        <h1>{draft.name}</h1>
        {draft.description && <p className="ws-skill-description">{draft.description}</p>}
        {!old && !published && skill.publishedRevision !== null && (
          <p className="ws-skill-publication">
            {formatCopy(copy.publishedVersion, { revision: skill.publishedRevision })}
          </p>
        )}
        {(editable || skill.publishedRevision !== null) && (
          <div className="ws-actions ws-skill-actions">
            {editable && (
              <button
                className="ws-button primary"
                disabled={busy}
                onClick={() => ws.authorSkill(skill)}
              >
                <WorkspaceIcon name="edit" size={14} />
                {old ? copy.editLatest : copy.editInChat}
              </button>
            )}
            {skill.publishedRevision !== null && (
              <button
                className={editable ? 'ws-button' : 'ws-button primary'}
                onClick={() => ws.useSkill(skill)}
              >
                <WorkspaceIcon name="chat" size={14} />
                {copy.useInChat}
              </button>
            )}
          </div>
        )}
        <SkillProperties copy={copy} draft={draft} />
      </header>
      <Tabs.List
        className="ws-view-tabs ws-canvas-tabs ws-skill-tabs"
        aria-label={copy.contentLabel}
      >
        <Tabs.Trigger value="instructions" className={activeTab === 'instructions' ? 'active' : ''}>
          {copy.tabInstructions}
        </Tabs.Trigger>
        {changes && (
          <Tabs.Trigger value="changes" className={activeTab === 'changes' ? 'active' : ''}>
            {diffCopy.changes}{' '}
            <span className="ws-diff-tab-stats">
              +{changes.addedWords} −{changes.removedWords}
            </span>
          </Tabs.Trigger>
        )}
        <Tabs.Trigger value="followed" className={activeTab === 'followed' ? 'active' : ''}>
          {copy.tabFollowedIn}
          {answers > 0 && <em>{answers}</em>}
        </Tabs.Trigger>
        {history.length > 1 && (
          <Tabs.Trigger value="versions" className={activeTab === 'versions' ? 'active' : ''}>
            {copy.tabVersions}
            <em>{history.length}</em>
          </Tabs.Trigger>
        )}
        {target.activityMessageId && (
          <Tabs.Trigger value="activity" className={activeTab === 'activity' ? 'active' : ''}>
            {canvasCopy.activity}
          </Tabs.Trigger>
        )}
      </Tabs.List>
      <Tabs.Content
        value={activeTab === 'activity' ? 'instructions' : activeTab}
        forceMount
        hidden={activeTab === 'activity'}
        className="ws-canvas-body ws-skill-document"
        ref={content}
        onMouseUp={selectPassage}
        onKeyUp={selectPassage}
      >
        {activeTab === 'changes' && changes ? (
          <DocumentDiff model={changes} />
        ) : activeTab === 'followed' ? (
          <FollowedIn copy={copy} links={links} failed={failed} />
        ) : activeTab === 'versions' ? (
          <Versions
            copy={copy}
            skill={skill}
            history={history}
            viewing={draft.revision}
            busy={busy}
            onView={(revision) => {
              setViewing(revision === skill.currentRevision ? null : revision);
              tab('instructions');
            }}
            onRestore={() => void restore()}
          />
        ) : (
          <>
            <WorkspaceMarkdown text={draft.instructions} />
            <div className="ws-skill-manage">
              <DeleteSkill key={`delete-${skill.id}`} skill={skill} disabled={busy} />
              {skill.canUnpublish &&
                skill.publishedRevision !== null &&
                (withdrawing ? (
                  <div className="ws-skill-withdrawal">
                    <p>{copy.unpublishQuestion}</p>
                    <div className="ws-actions">
                      <button
                        className="ws-button"
                        disabled={busy}
                        onClick={() => setWithdrawing(false)}
                      >
                        {copy.cancel}
                      </button>
                      <button
                        className="ws-button"
                        disabled={busy}
                        onClick={() => void unpublish()}
                      >
                        {busy ? copy.unpublishing : copy.confirmUnpublish}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="ws-text-button ws-skill-unpublish"
                    onClick={() => setWithdrawing(true)}
                  >
                    {copy.unpublish}
                  </button>
                ))}
            </div>
            {old && skill.canEdit && (
              <button
                type="button"
                className="ws-text-button ws-skill-restore"
                disabled={busy}
                onClick={() => void restore()}
              >
                {copy.restore}
              </button>
            )}
          </>
        )}
        {selection && activeTab === 'instructions' && skill.canEdit && (
          <button
            className="ws-selection-action ws-button"
            onClick={() => {
              ws.send(`Please adjust this passage:\n\n“${selection}”\n\n`, {
                ...ws.turnContext,
                intent: {
                  mode: 'skill-author',
                  skillId: skill.id,
                  revision: skill.currentRevision,
                },
                selection: {
                  artifactId: skill.id,
                  revision: skill.currentRevision,
                  text: selection,
                },
              });
              setSelection('');
            }}
          >
            {copy.discussPassage} <WorkspaceIcon name="chat" size={14} />
          </button>
        )}
        {error && (
          <p className="ws-error" role="alert">
            {error}
          </p>
        )}
      </Tabs.Content>
      {target.activityMessageId && (
        <Tabs.Content
          value="activity"
          forceMount
          hidden={activeTab !== 'activity'}
          className="ws-canvas-body ws-skill-document"
        >
          <CanvasActivityTab target={target} />
        </Tabs.Content>
      )}
      {!old && (
        <SkillFooter
          copy={copy}
          skill={skill}
          published={published}
          answers={answers}
          canPublish={canPublish}
          busy={busy}
          hidden={activeTab === 'activity'}
          company={company?.company?.name ?? ''}
          onPublish={() => void publish()}
        />
      )}
    </Tabs.Root>
  );
}

function SkillFooter({
  copy,
  skill,
  published,
  answers,
  canPublish,
  busy,
  hidden,
  company,
  onPublish,
}: {
  copy: Copy;
  skill: CompanySkill;
  published: boolean;
  answers: number;
  canPublish: boolean;
  busy: boolean;
  hidden: boolean;
  company: string;
  onPublish: () => void;
}) {
  if (published)
    return (
      <CanvasFooter hidden={hidden}>
        <div>
          <strong>{company ? formatCopy(copy.publishedFor, { company }) : copy.published}</strong>
          {answers > 0 ? (
            <p>{answersLine(copy, answers)}</p>
          ) : (
            skill.canEdit && <p>{copy.editStaysDraft}</p>
          )}
        </div>
      </CanvasFooter>
    );
  if (!skill.canEdit) return null;
  return (
    <CanvasFooter hidden={hidden}>
      <div>
        <strong>{skill.visibility === 'private' ? copy.draftOnlyYou : copy.draftShared}</strong>
        <p>{skill.publishedRevision === null ? copy.publishHint : copy.newerDraftHint}</p>
      </div>
      <div className="ws-actions">
        <button className="ws-button primary" disabled={busy} onClick={onPublish}>
          {busy ? copy.publishing : canPublish ? copy.publish : copy.requestPublication}
        </button>
      </div>
    </CanvasFooter>
  );
}

type FollowedHow = 'picked' | 'agent' | 'found' | 'read';

/** The exact version an answer followed, and why it was read. */
function FollowedSkill({
  skill,
  revision,
  followed,
  need,
}: {
  skill: CompanySkill;
  revision?: string;
  followed: FollowedHow;
  need?: string;
}) {
  const copy = useNamespaceCopy(skillsCopy);
  const { history, state, retry } = useSkillHistory(skill);
  const wanted = Number(revision);
  const exact =
    history.find((snapshot) => snapshot.revision === wanted) ??
    [skill.published, skill.draft].find((snapshot) => snapshot?.revision === wanted) ??
    null;
  // An earlier version comes with the history: until it is read, show no
  // stand-in instructions, and say a version is gone only once it was read.
  const settled = exact !== null || state === 'ready';
  const shown = exact ?? skill.published ?? skill.draft;
  const latest = skill.publishedRevision ?? skill.currentRevision;
  const newer = exact && latest > wanted ? latest : null;
  const openInSkills = (version?: number) => {
    window.location.hash = routeFor('skills', {
      kind: 'skill',
      id: skill.id,
      ...(version ? { revision: String(version) } : {}),
    });
  };
  return (
    <article className="ws-canvas-body ws-followed-skill">
      <div className="ws-eyebrow">
        {formatCopy(copy.followedEyebrow, { revision: settled ? shown.revision : wanted })}
      </div>
      <h1>{shown.name}</h1>
      {settled && shown.description && (
        <p className="ws-followed-skill-summary">{shown.description}</p>
      )}
      <div className="ws-followed-skill-context">
        <p role="status">
          {formatCopy(copy.followedContext, { revision: wanted || shown.revision })}
        </p>
        <small>
          {followedReason(copy, followed, need)}. {copy.followedRecordNote}
        </small>
      </div>
      {!exact && state === 'failed' && (
        <div className="ws-followed-skill-error" role="alert">
          <span>{formatCopy(copy.versionLoadFailed, { revision: wanted })}</span>
          <button type="button" className="ws-text-button" onClick={retry}>
            {copy.retryVersion}
          </button>
        </div>
      )}
      {!exact && state === 'ready' && Number.isInteger(wanted) && wanted > 0 && (
        <p className="ws-followed-skill-newer">
          {formatCopy(copy.versionUnavailable, { revision: wanted })}
        </p>
      )}
      {newer !== null && (
        <p className="ws-followed-skill-newer">
          <span>{formatCopy(copy.newerVersion, { revision: newer })}</span>
          <button type="button" className="ws-text-button" onClick={() => openInSkills(newer)}>
            {formatCopy(copy.viewNewer, { revision: newer })}
          </button>
        </p>
      )}
      {settled && (
        <div className="ws-followed-skill-body">
          <WorkspaceMarkdown text={shown.instructions} />
        </div>
      )}
      <button
        type="button"
        className="ws-text-button ws-followed-skill-open"
        onClick={() => openInSkills()}
      >
        {copy.openInSkills}
      </button>
    </article>
  );
}

export function SkillCanvas({
  skill,
  target,
}: {
  skill: CompanySkill | null;
  target: CanvasTarget;
}) {
  const copy = useNamespaceCopy(skillsCopy);
  if (!skill) return <NewObjectCanvas title={copy.newSkillTitle} body={copy.newSkillBody} />;
  if ('followed' in target && target.followed)
    return (
      <FollowedSkill
        skill={skill}
        revision={target.revision}
        followed={target.followed}
        need={target.need}
      />
    );
  return <SkillDocument skill={skill} target={target} />;
}
